import React from "react";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";
import { fetchNavbarAndFooter } from "lib/fetchers/common";

import LeaderboardsView from "content-ui/leaderboards";
import { fetchLeaderboardsPage } from "content-ui/leaderboards/fetcher";
import type { LeaderboardsPageData } from "lib/types/leaderboards";
import { SEO_SIFTED_ON_PINK_IMAGE } from "lib/constants/shared";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";

export async function getServerSideProps({ res, params, preview = false }) {
  const [menuItems, pageData] = await Promise.all([
    fetchNavbarAndFooter(),
    fetchLeaderboardsPage({ preview }),
  ]);

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=10, stale-while-revalidate=59"
  );

  return {
    props: {
      previewMode: preview,
      pageData,
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}

type PageProps = {
  pageData: LeaderboardsPageData;
  previewMode: boolean;
};

const LeaderboardsPage = (props: PageProps) => {
  const title = props?.pageData?.seo?.title || "Leaderboards | Sifted";
  const description = props?.pageData?.seo?.description || "";
  const seoImage = props?.pageData?.seo?.image?.url || SEO_SIFTED_ON_PINK_IMAGE;

  return (
    <>
      <Head>
        {generateNextSeo({
          title: title,
          description: description,
          canonical: buildCanonicalUrl("/leaderboards"),
          openGraph: {
            title: title,
            description: description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      {props.previewMode ? (
        <PreviewModeBanner exitSlug={`/leaderboards}`} />
      ) : null}
      <ErrorBoundary>
        <LeaderboardsView data={props.pageData} />
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default LeaderboardsPage;
